/**
 * The price lines of «ویزیت آنلاین سازمانی» (search cards, profile box) — only for the one rolled-out
 * account (rollout.ts). Everyone else, and the server, gets exactly today's value, untouched.
 */
import { withOnlineVisitVat } from '@/common/utils/onlineVisitPriceWithVat';
import { ReactNode } from 'react';
import { corporateProfilePrice } from './components/profile';
import { CorporateCardPrice } from './components/search';
import { getCoverage } from './functions/coverage';
import { type CorporateMember, useCorporateMember } from './rollout';

type CardItem = { price?: unknown; consult_services?: Array<{ free_price?: unknown }> } | undefined;

const toLatinNumber = (text: string) => {
  const digits = text.match(/[0-9۰-۹][0-9۰-۹,٬]*/)?.[0];
  if (!digits) return 0;
  return parseInt(digits.replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/[,٬]/g, ''), 10) || 0;
};

/** The fee on the card, in toman, the same way `onlineVisitPriceLabel` builds it. */
const cardFeeToman = (item: CardItem, includeVat: boolean) => {
  if (item?.price) return withOnlineVisitVat(toLatinNumber(String(item.price)), includeVat);
  const freePriceRial = parseInt(String(item?.consult_services?.[0]?.free_price ?? ''), 10);
  return freePriceRial > 0 ? Math.floor(withOnlineVisitVat(freePriceRial, includeVat) / 10) : 0;
};

/**
 * Search card «ویزیت آنلاین: …»: the member sees the fee struck through and «رایگان». `member` comes from
 * `useCorporateMember()` in the list; when it is `null` (everyone else, the server) `label` is returned as is.
 */
export const corporateCardPrice = (label: ReactNode, item: CardItem, includeVat: boolean, member: CorporateMember | null): ReactNode => {
  if (!member || !label) return label;
  const toman = cardFeeToman(item, includeVat);
  if (toman <= 0) return label;
  return <CorporateCardPrice coverage={getCoverage(member.plan, { priceToman: toman, inNetwork: true })} />;
};

/** Profile online-visit box price (`priceSlot`), or `null` to keep the box's own price. */
export const useCorporateProfilePrice = (freePriceRial: unknown, includeVat: boolean): ReactNode => {
  const member = useCorporateMember();
  const rial = parseInt(String(freePriceRial ?? ''), 10);
  if (!member || !(rial > 0)) return null;
  return corporateProfilePrice(getCoverage(member.plan, { priceToman: Math.round(withOnlineVisitVat(rial, includeVat) / 10), inNetwork: true }));
};
